import React from 'react';
import StyledTable from '@shared/components/StyledTable';
import constants from '@shared-styles/constants.module.scss';
import { isNumKeyValid } from '../../../../../utils/numberExtensions';
import DELTA_TRENDS from '../../../constants/deltaTrends';

const DELTA_TRENDS_TO_TEXT_MAP = {
  [DELTA_TRENDS.SUSTAINED_STRENGTH]: 'Sustained Strength',
  [DELTA_TRENDS.SUSTAINED_CHALLENGE]: 'Sustained Challenge',
  [DELTA_TRENDS.EMERGING_STRENGTH]: 'Emerging Strength',
  [DELTA_TRENDS.EMERGING_CHALLENGE]: 'Emerging Challenge',
  [DELTA_TRENDS.RESOLVED_CHALLENGE]: 'Resolved Challenge',
  [DELTA_TRENDS.PAST_STRENGTH]: 'Past Strength'
};

const DELTA_TRENDS_TO_COLOR_MAP = {
  [DELTA_TRENDS.SUSTAINED_STRENGTH]: constants.isacaMediumBlueWithOpacity,
  [DELTA_TRENDS.SUSTAINED_CHALLENGE]: constants.isacaOrangeWithOpacity,
  [DELTA_TRENDS.EMERGING_STRENGTH]: constants.isacaMediumBlueWithLightOpacity,
  [DELTA_TRENDS.EMERGING_CHALLENGE]: constants.isacaOrangeWithLightOpacity,
  [DELTA_TRENDS.PAST_STRENGTH]: constants.whiteColor,
  [DELTA_TRENDS.RESOLVED_CHALLENGE]: constants.whiteColor
};

/**
 * @typedef {object} DeltaReportTableProps
 * @property {Array<Object>} [deltaInstances]
 * @property {Array<*>} [data]
 * @property {boolean} [loading]
 */

/**
 * @param {DeltaReportTableProps} props
 */
const DeltaReportTable = props => {
  const { deltaInstances, data = [], loading = false } = props;

  const [firstInstance, secondInstance] = deltaInstances;
  const firstInstanceLabel = firstInstance.label;
  const secondInstanceLabel = secondInstance.label;

  const columns = [
    {
      title: 'Practice',
      dataIndex: 'practice',
      render: (text, record) => <div title={record.hoveringText}>{text}</div>,
      key: 'practice',
      sorter: (a, b) => a.practice.localeCompare(b.practice)
    },
    {
      title: 'Trend',
      dataIndex: 'trend',
      onCell: record => {
        return {
          style: {
            backgroundColor: DELTA_TRENDS_TO_COLOR_MAP[record.trend]
          }
        };
      },
      render: val => DELTA_TRENDS_TO_TEXT_MAP[val],
      key: 'trend',
      sorter: (a, b) => a.trend - b.trend,
      width: '12%'
    },
    {
      title: `${firstInstanceLabel} Satisfied`,
      dataIndex: 'firstInstanceSatisfiedPercentage',
      render: text => text + '%',
      key: 'firstInstanceSatisfiedPercentage',
      sorter: (a, b) => a.firstInstanceSatisfiedPercentage - b.firstInstanceSatisfiedPercentage
    },
    {
      title: `${secondInstanceLabel} Satisfied`,
      dataIndex: 'secondInstanceSatisfiedPercentage',
      render: text => text + '%',
      key: 'secondInstanceSatisfiedPercentage',
      sorter: (a, b) => a.secondInstanceSatisfiedPercentage - b.secondInstanceSatisfiedPercentage
    },
    {
      title: 'Delta Satisfied',
      dataIndex: 'deltaSatisfied',
      render: text => text + '%',
      key: 'deltaSatisfied',
      sorter: (a, b) => a.deltaSatisfied - b.deltaSatisfied
    },
    {
      title: `${firstInstanceLabel} Aggregate`,
      dataIndex: 'firstInstanceAggregatePercentage',
      render: text => text + '%',
      key: 'firstInstanceAggregatePercentage',
      sorter: (a, b) => a.firstInstanceAggregatePercentage - b.firstInstanceAggregatePercentage
    },
    {
      title: `${secondInstanceLabel} Aggregate`,
      dataIndex: 'secondInstanceAggregatePercentage',
      render: text => text + '%',
      key: 'secondInstanceAggregatePercentage',
      sorter: (a, b) => a.secondInstanceAggregatePercentage - b.secondInstanceAggregatePercentage
    },
    {
      title: 'Delta Aggregate',
      dataIndex: 'deltaAggregate',
      render: text => text + '%',
      key: 'deltaAggregate',
      sorter: (a, b) => a.deltaAggregate - b.deltaAggregate
    },
    {
      title: `${firstInstanceLabel} Count`,
      dataIndex: 'firstInstanceCount',
      render: text => text,
      key: 'firstInstanceCount',
      sorter: (a, b) => a.firstInstanceCount - b.firstInstanceCount
    },
    {
      title: `${secondInstanceLabel} Count`,
      dataIndex: 'secondInstanceCount',
      render: text => text,
      key: 'secondInstanceCount',
      sorter: (a, b) => a.secondInstanceCount - b.secondInstanceCount
    }
  ];

  const handleKeyDown = e => {
    if (!isNumKeyValid(e)) {
      e.preventDefault();
    }
  };

  return (
    <StyledTable
      rowKey='practice'
      className='delta-report-table'
      dataSource={data}
      columns={columns}
      pagination={{
        showSizeChanger: {
          onInputKeyDown: handleKeyDown
        }
      }}
      showSorterTooltip={false}
      loading={loading}
    />
  );
};

export default DeltaReportTable;
